/**
 * @file ArchivedChallengesPage.jsx
 * @description Page for viewing archived challenges
 */

import React, { useState, useEffect } from "react";
import { useUserAuth } from "../UserAuth";
import { db } from "../firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { MaterialHeader } from "./MaterialHeader";
import { useViewportHeight } from "../utils/viewportUtils";
import { Icon } from "./Icon";

export const ArchivedChallengesPage = () => {
  const { user } = useUserAuth();
  const viewportHeight = useViewportHeight();
  const [archivedChallenges, setArchivedChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchArchivedChallenges();
    } else {
      setLoading(false);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArchivedChallenges = async () => {
    try {
      setLoading(true);
      
      // Get user's challenge refs from users/{uid}/challenges
      const userChallengesRef = collection(db, "users", user.uid, "challenges");
      const userChallengesSnapshot = await getDocs(userChallengesRef);
      
      // Get the actual challenge data from challenges collection
      const challengePromises = userChallengesSnapshot.docs.map(async (userChallengeDoc) => {
        const challengeId = userChallengeDoc.id;
        const userChallengeData = userChallengeDoc.data();
        
        // Only process archived challenges
        if (!userChallengeData.archived) {
          return null;
        }
        
        // Fetch the actual challenge data
        const challengeDocRef = doc(db, "challenges", challengeId);
        const challengeDoc = await getDoc(challengeDocRef);
        
        if (challengeDoc.exists()) {
          return {
            id: challengeId,
            ...challengeDoc.data(),
            ...userChallengeData // Include user-specific data like progress
          };
        }
        return null;
      });
      
      const challenges = (await Promise.all(challengePromises)).filter(Boolean);
      
      // Sort by lastUpdated (most recent first) or by start_date if no lastUpdated
      challenges.sort((a, b) => {
        const aTime = a.lastUpdated || new Date(a.start_date).getTime();
        const bTime = b.lastUpdated || new Date(b.start_date).getTime();
        return bTime - aTime;
      });
      
      setArchivedChallenges(challenges);
    } catch (error) {
      console.error("Error fetching archived challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    window.location.href = '/';
  };

  const getProgressPercentage = (progress) => {
    if (!progress) return 0;
    const completed = progress.split('').filter(p => p === '1').length;
    return Math.round((completed / progress.length) * 100);
  };

  return (
    <>
      <MaterialHeader 
        variant="close" 
        title="Archived Challenges"
        onClose={handleClose}
        hideSettings={true}
      />
      <div className="app-content" style={{ 
        height: `calc(${viewportHeight} - 64px)`, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="container" style={{ 
          maxWidth: '1200px',
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px'
        }}>

        {/* Content */}
        <div style={{ flex: '1', overflow: 'auto', minHeight: 0 }}>
          {!user ? (
            <md-elevated-card style={{ padding: '48px', textAlign: 'center' }}>
              <Icon
                name="signIn"
                size="hero"
                style={{ 
                  color: 'var(--md-sys-color-outline)',
                  marginBottom: '24px'
                }}
              />
              <h2 style={{ 
                fontSize: 'var(--md-sys-typescale-headline-medium-size)',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '16px'
              }}>
                Sign In Required
              </h2>
              <p style={{ 
                color: 'var(--md-sys-color-on-surface-variant)',
                marginBottom: '32px',
                maxWidth: '500px',
                margin: '0 auto 32px'
              }}>
                Please sign in to view your archived challenges.
              </p>
              <md-filled-button href="/">
                <Icon name="home" slot="icon" />
                Back to Home
              </md-filled-button>
            </md-elevated-card>
          ) : loading ? (
            <md-elevated-card style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                color: 'var(--md-sys-color-on-surface-variant)'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid var(--md-sys-color-primary)',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Loading archived challenges...
              </div>
            </md-elevated-card>
          ) : archivedChallenges.length === 0 ? (
            <md-elevated-card style={{ padding: '48px', textAlign: 'center' }}>
              <Icon
                name="archive"
                size="hero"
                style={{ 
                  color: 'var(--md-sys-color-outline)',
                  marginBottom: '24px'
                }}
              />
              <h2 style={{ 
                fontSize: 'var(--md-sys-typescale-headline-medium-size)',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '16px'
              }}>
                No Archived Challenges
              </h2>
              <p style={{ 
                color: 'var(--md-sys-color-on-surface-variant)',
                marginBottom: '32px',
                maxWidth: '500px',
                margin: '0 auto 32px'
              }}>
                You haven't archived any challenges yet. Challenges you complete or choose to archive will appear here.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <md-filled-button href="/">
                  <Icon name="home" slot="icon" />
                  Back to Home
                </md-filled-button>
                <md-outlined-button href="/join-challenges">
                  <Icon name="search" slot="icon" />
                  Find Challenges
                </md-outlined-button>
              </div>
            </md-elevated-card>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '24px',
              padding: '0 0 24px 0'
            }}>
              {archivedChallenges.map((challenge) => (
                <md-elevated-card key={challenge.id} style={{ padding: '24px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <h3 style={{
                      fontSize: 'var(--md-sys-typescale-headline-small-size)',
                      fontWeight: 'var(--md-sys-typescale-headline-small-weight)',
                      color: 'var(--md-sys-color-on-surface)',
                      margin: '0 0 8px 0'
                    }}>
                      {challenge.challenge_name}
                    </h3>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <p style={{
                      color: 'var(--md-sys-color-on-surface-variant)',
                      fontSize: 'var(--md-sys-typescale-body-medium-size)',
                      margin: '0 0 8px 0'
                    }}>
                      <strong>Books:</strong> {challenge.books.join(', ')}
                    </p>
                    <p style={{
                      color: 'var(--md-sys-color-on-surface-variant)',
                      fontSize: 'var(--md-sys-typescale-body-medium-size)',
                      margin: '0 0 8px 0'
                    }}>
                      <strong>Reading Days:</strong> {challenge.days.join(', ')}
                    </p>
                    <p style={{
                      color: 'var(--md-sys-color-on-surface-variant)',
                      fontSize: 'var(--md-sys-typescale-body-medium-size)',
                      margin: '0 0 12px 0'
                    }}>
                      <strong>Started:</strong> {new Date(challenge.start_date).toLocaleDateString()}
                    </p>
                    
                    {challenge.progress && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            fontSize: 'var(--md-sys-typescale-body-small-size)',
                            color: 'var(--md-sys-color-on-surface-variant)'
                          }}>
                            Progress
                          </span>
                          <span style={{
                            fontSize: 'var(--md-sys-typescale-body-small-size)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                            fontWeight: '500'
                          }}>
                            {getProgressPercentage(challenge.progress)}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: 'var(--md-sys-color-surface-container-high)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${getProgressPercentage(challenge.progress)}%`,
                            height: '100%',
                            backgroundColor: 'var(--md-sys-color-primary)',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <md-outlined-button 
                      href={`/challenge?id=${challenge.id}`}
                      style={{ flex: '1' }}
                    >
                      <Icon name="view" slot="icon" />
                      View Details
                    </md-outlined-button>
                  </div>
                </md-elevated-card>
              ))}
            </div>
          )}
        </div>

        {/* Add CSS for spinner animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        </div>
      </div>
    </>
  );
};
